import { CustomerFormValues } from "@/api/types";
import type { Customer, CustomerBlockHistory } from "@/types/customer-type";
import { ApiResponse, PageContent } from "@/types/api-response";
import { apiClient } from "@/api/api-client";
import { Participant } from "@/types/fb-type";
import { OrderHistory, TOrderWithRelation } from "@/types/order-type";

// Create a new customer
export async function createCustomer(
  customer: CustomerFormValues
): Promise<ApiResponse<Customer>> {
  const response = await apiClient.post<Customer, CustomerFormValues>(
    "/customers",
    customer
  );

  return response;
}

// Get all customers with pagination and search support
export async function getCustomers(params?: {
  page?: number;
  size?: number;
  searchTerm?: string | null;
}): Promise<ApiResponse<PageContent<Customer>>> {
  const { searchTerm, page = 0, size = 20 } = params || {};

  if (searchTerm?.trim()) {
    // Use search endpoint with pagination
    const response = await apiClient.get<PageContent<Customer>, object>(
      "/customers/search",
      {
        searchTerm: searchTerm.trim(),
        page,
        size,
      }
    );
    return response;
  } else {
    // Use regular endpoint with pagination
    const response = await apiClient.get<PageContent<Customer>, object>(
      "/customers",
      {
        page,
        size,
      }
    );
    return response;
  }
}

// Get customer by ID
export async function getCustomer(id: string): Promise<ApiResponse<Customer>> {
  const response = await apiClient.get<Customer, object>(`/customers/${id}`);
  return response;
}

// Update customer by ID
export async function updateCustomer(
  id: string,
  customer: CustomerFormValues
): Promise<ApiResponse<Customer>> {
  const response = await apiClient.put<Customer, CustomerFormValues>(
    `/customers/${id}`,
    customer
  );
  return response;
}
export async function updatePhone(id: string, phone: string) {
  const data = {
    value: phone,
  };
  const response = await apiClient.post<{}, {}>(`/customers/${id}/phone`, data);
  return response;
}

export async function updateAddress(id: string, orderId: string | null, address: string) {
  const data = {
    address,
    orderId
  };
  const response = await apiClient.post<{}, {}>(
    `/customers/${id}/address`,
    data
  );
  return response;
}

// Delete customer by ID
export async function deleteCustomer(
  id: string
): Promise<ApiResponse<Customer>> {
  const response = await apiClient.delete<Customer>(`/customers/${id}`);
  return response;
}

export async function createCustomerIfNotExist(user: Participant) {
  const response = await apiClient.post<Customer, Participant>(
    `/customers/create`,
    user
  );
  return response;
}

export async function getPendingOrders(customer: Customer) {
  const response = await apiClient.get<TOrderWithRelation[], {}>(
    `/customers/${customer.id}/pending-orders`,
    {}
  );
  return response;
}

export async function getOrderHistory(customer: Customer) {
  const response = await apiClient.get<OrderHistory, {}>(
    `/customers/${customer.id}/orders`,
    {}
  );
  return response;
}

// Block customer by ID (reason required)
export async function blockCustomer(
  id: string,
  reason: string
): Promise<ApiResponse<Customer>> {
  return apiClient.post<Customer, { reason: string }>(
    `/customers/${id}/block`,
    { reason: reason.trim() }
  );
}

// Unblock customer by ID (reason optional)
export async function unblockCustomer(
  id: string,
  reason?: string
): Promise<ApiResponse<Customer>> {
  return apiClient.post<Customer, { reason?: string }>(
    `/customers/${id}/unblock`,
    { reason: reason?.trim() || "Unblocked by staff" }
  );
}

// Get block / unblock / rejected order history for a customer
export async function getCustomerBlockHistory(
  id: string
): Promise<ApiResponse<CustomerBlockHistory[]>> {
  return apiClient.get<CustomerBlockHistory[], object>(
    `/customers/${id}/block-history`
  );
}

